// Panel NetInspect — inspecteur HTTPS (proxy de débogage type Fiddler/Charles).
//
// Flow :
//   1. Operator clique "Installer le certificat" → tool netinspect_setup
//      → génère et installe la CA racine dans le magasin Root LocalMachine.
//   2. Operator saisit l'allowlist de domaines + port + option proxy système,
//      puis clique "Démarrer" → tool netinspect_start {allowlist, port, setSystemProxy}
//      Retourne { proxyId, jobId, port }.
//   3. Le plugin C# stream chaque transaction HTTP(S) via IJobController.Log()
//      → envelope live/plugin/job.event capté par ws.on().
//   4. Operator clique "Arrêter" → tool netinspect_stop (restaure le proxy système).
//   5. Au démontage du composant : auto-stop du proxy actif (cleanup).
//
// Affichage : table des transactions (les 1000 dernières pour borner la RAM UI),
// recherche substring, stats live.

import { createSignal, createMemo, onCleanup, For, Show, type Component } from "solid-js";
import { PanelLayout, type PanelStatus } from "@/shared/panel-base";
import { useToolClient } from "@/shared/tool-client";
import { useWs } from "@/shared/ws";
import { isWsEnvelope } from "@/shared/ws-protocol";
import { registerPanel } from "@/panel-registry";
import "./netinspect.css";

interface Transaction {
  seq: number;
  at: string;
  scheme: string; // "http" | "https" | "tunnel"
  method?: string;
  host: string;
  url?: string;
  status?: number;
  reqBytes?: number;
  respBytes?: number;
  durationMs?: number;
  contentType?: string | null;
  error?: string | null;
}

const MAX_DISPLAY = 1000; // évite de saturer la RAM UI

const NetInspectPanel: Component = () => {
  const client = useToolClient();
  const ws = useWs();

  const [proxyId, setProxyId] = createSignal<string | null>(null);
  const [_jobId, setJobId] = createSignal<string | null>(null);
  const [txs, setTxs] = createSignal<Transaction[]>([]);
  const [status, setStatus] = createSignal<PanelStatus | undefined>();
  const [busy, setBusy] = createSignal(false);
  const [caInstalled, setCaInstalled] = createSignal<boolean | null>(null);

  // Config (avant démarrage du proxy)
  const [allowlistText, setAllowlistText] = createSignal("");
  const [port, setPort] = createSignal("8888");
  const [setSystemProxy, setSetSystemProxy] = createSignal(true);

  // Filtre d'affichage (post-capture)
  const [searchQuery, setSearchQuery] = createSignal("");

  let offWsListener: (() => void) | null = null;

  // Souscription aux events live/plugin/job.event filtrés par jobId courant.
  function subscribeJob(jid: string) {
    offWsListener?.();
    offWsListener = ws.on(
      (m) =>
        isWsEnvelope(m, "live", "plugin", "job.event") &&
        (m as { payload?: { pluginId?: string; jobId?: string } }).payload?.pluginId === "netinspect" &&
        (m as { payload?: { jobId?: string } }).payload?.jobId === jid,
      (m: unknown) => {
        const msg = m as { payload?: { event?: { type?: string; line?: string } } };
        const p = msg.payload?.event;
        if (!p || p.type !== "log" || !p.line) return;
        try {
          const obj = JSON.parse(p.line);
          if (obj.type === "tx" || obj.type === "tunnel") {
            setTxs((cur) => {
              const tx: Transaction = {
                seq: obj.seq ?? cur.length + 1,
                at: obj.at ?? new Date().toISOString(),
                scheme: obj.type === "tunnel" ? "tunnel" : obj.scheme || "https",
                method: obj.method,
                host: obj.host || "—",
                url: obj.url,
                status: obj.status,
                reqBytes: obj.reqBytes,
                respBytes: obj.respBytes,
                durationMs: obj.durationMs,
                contentType: obj.contentType,
                error: obj.error,
              };
              const next = [...cur, tx];
              return next.length > MAX_DISPLAY ? next.slice(-MAX_DISPLAY) : next;
            });
          } else if (obj.type === "stopped") {
            setStatus({
              text: `Arrêté : ${obj.reason} (${obj.stats?.transactions ?? 0} transactions)`,
              level: "info",
            });
            setProxyId(null);
            setJobId(null);
          } else if (obj.type === "error") {
            setStatus({ text: `Erreur : ${obj.error}`, level: "danger" });
          } else if (obj.type === "warning") {
            setStatus({ text: `Attention : ${obj.message}`, level: "warn" });
          }
        } catch {
          // Ignore les lignes non-JSON
        }
      },
    );
  }

  async function installCert() {
    if (busy()) return;
    setBusy(true);
    setStatus({ text: "Installation du certificat racine…", level: "info" });
    try {
      const r = (await client.call("netinspect_setup", {}, { timeoutMs: 20_000 })) as {
        caInstalled: boolean;
        caThumbprint: string;
      };
      setCaInstalled(r.caInstalled);
      setStatus({
        text: r.caInstalled
          ? `Certificat installé (${r.caThumbprint?.slice(0, 16) ?? ""}…)`
          : "Certificat généré mais non confirmé dans le magasin",
        level: r.caInstalled ? "info" : "warn",
      });
    } catch (e: unknown) {
      setStatus({ text: `Échec setup : ${e instanceof Error ? e.message : String(e)}`, level: "danger" });
    } finally {
      setBusy(false);
    }
  }

  function parseAllowlist(): string[] {
    return allowlistText()
      .split(/[\n,]/)
      .map((d) => d.trim())
      .filter((d) => d.length > 0);
  }

  async function startProxy() {
    if (busy() || proxyId()) return;
    const allowlist = parseAllowlist();
    if (allowlist.length === 0) {
      setStatus({ text: "Saisir au moins un domaine dans l'allowlist.", level: "danger" });
      return;
    }
    const p = parseInt(port().trim(), 10);
    if (isNaN(p) || p < 1 || p > 65535) {
      setStatus({ text: "Port invalide.", level: "danger" });
      return;
    }
    setBusy(true);
    setStatus({ text: "Démarrage du proxy…", level: "info" });
    setTxs([]);
    try {
      const r = (await client.call(
        "netinspect_start",
        { allowlist, port: p, setSystemProxy: setSystemProxy() },
        { timeoutMs: 20_000 },
      )) as { proxyId: string; jobId: string; port: number };
      if (!r.proxyId || !r.jobId) throw new Error("Réponse invalide");
      setProxyId(r.proxyId);
      setJobId(r.jobId);
      subscribeJob(r.jobId);
      setStatus({
        text: `Proxy actif sur 127.0.0.1:${r.port} — ${allowlist.length} domaine(s) interceptés`,
        level: "info",
      });
    } catch (e: unknown) {
      setStatus({ text: `Échec : ${e instanceof Error ? e.message : String(e)}`, level: "danger" });
    } finally {
      setBusy(false);
    }
  }

  async function stopProxy(silent = false) {
    if (!proxyId()) return;
    if (!silent) setStatus({ text: "Arrêt en cours…", level: "info" });
    try {
      await client.call("netinspect_stop", {}, { timeoutMs: 10_000 });
    } catch {
      // ignore — le plugin peut déjà avoir stoppé
    }
    setProxyId(null);
    setJobId(null);
    offWsListener?.();
    offWsListener = null;
  }

  function clearTxs() {
    setTxs([]);
  }

  // Filtre d'affichage — recherche substring sur host/url/method.
  const filteredTxs = createMemo(() => {
    const q = searchQuery().trim().toLowerCase();
    const list = txs();
    if (!q) return list;
    return list.filter(
      (t) =>
        t.host.toLowerCase().includes(q) ||
        (t.url ?? "").toLowerCase().includes(q) ||
        (t.method ?? "").toLowerCase().includes(q) ||
        String(t.status ?? "").includes(q),
    );
  });

  const totalBytes = createMemo(() => txs().reduce((acc, t) => acc + (t.reqBytes ?? 0) + (t.respBytes ?? 0), 0));
  const totalKb = createMemo(() => (totalBytes() / 1024).toFixed(1));

  // ─── Lifecycle ──────────────────────────────────────────────────────────
  // CRUCIAL : au démontage du composant, on STOP le proxy actif (sinon on
  // laisse le listener ouvert et potentiellement le proxy système détourné).
  onCleanup(() => {
    if (proxyId()) void stopProxy(true);
    offWsListener?.();
  });

  function statusClass(code?: number): string {
    if (!code) return "netinspect-status-0";
    return `netinspect-status-${Math.floor(code / 100)}`;
  }

  const toolbar = (
    <>
      <Show
        when={!proxyId()}
        fallback={
          <button class="btn btn--xs btn--danger" disabled={busy()} onClick={() => void stopProxy()}>
            ■ Arrêter
          </button>
        }
      >
        <button class="btn btn--xs btn--primary" disabled={busy()} onClick={() => void startProxy()}>
          ▶ Démarrer
        </button>
      </Show>
      <button class="btn btn--xs" onClick={clearTxs} title="Vider l'affichage">
        ✕ Effacer
      </button>
    </>
  );

  return (
    <PanelLayout title="Inspecteur HTTPS" toolbar={toolbar} status={status()}>
      <div class="netinspect-panel">
        {/* ─── Setup CA ─── */}
        <div class="netinspect-setup">
          <button class="btn btn--xs" disabled={busy()} onClick={() => void installCert()}>
            🛡 Installer le certificat
          </button>
          <Show when={caInstalled() !== null}>
            <span class={caInstalled() ? "netinspect-ca-ok" : "netinspect-ca-missing"}>
              {caInstalled() ? "✓ CA installée" : "⚠ CA non installée"}
            </span>
          </Show>
          <span class="netinspect-muted">
            Le déchiffrement HTTPS exige que la CA racine soit dans le magasin de confiance.
          </span>
        </div>

        {/* ─── Config (avant Start) ─── */}
        <div class="netinspect-config">
          <label>
            <span>Allowlist de domaines (un par ligne) :</span>
            <textarea
              class="netinspect-allowlist"
              placeholder={"example.com\napi.service.io"}
              value={allowlistText()}
              disabled={!!proxyId()}
              onInput={(e) => setAllowlistText(e.currentTarget.value)}
            />
          </label>
          <label>
            <span>Port :</span>
            <input
              class="panel-filter-input netinspect-port"
              type="text"
              placeholder="8888"
              value={port()}
              disabled={!!proxyId()}
              onInput={(e) => setPort(e.currentTarget.value)}
            />
          </label>
          <label class="netinspect-toggle">
            <input
              type="checkbox"
              checked={setSystemProxy()}
              disabled={!!proxyId()}
              onChange={(e) => setSetSystemProxy(e.currentTarget.checked)}
            />{" "}
            Proxy système
          </label>
        </div>

        {/* ─── Stats live ─── */}
        <div class="netinspect-stats">
          <div class="netinspect-stat">
            <span class="netinspect-stat-label">Transactions</span>
            <span class="netinspect-stat-value">{txs().length.toLocaleString()}</span>
          </div>
          <div class="netinspect-stat">
            <span class="netinspect-stat-label">Volume</span>
            <span class="netinspect-stat-value">{totalKb()} KB</span>
          </div>
          <Show when={proxyId()}>
            <div class="netinspect-stat">
              <span class="netinspect-stat-label">Statut</span>
              <span class="netinspect-stat-value netinspect-live">● LIVE</span>
            </div>
          </Show>
        </div>

        {/* ─── Recherche ─── */}
        <div class="netinspect-search">
          <input
            class="panel-filter-input"
            type="text"
            placeholder="Filtrer (host, URL, méthode, status) — recherche substring"
            value={searchQuery()}
            onInput={(e) => setSearchQuery(e.currentTarget.value)}
          />
          <span class="netinspect-search-count">
            {filteredTxs().length} / {txs().length}
          </span>
        </div>

        {/* ─── Table transactions ─── */}
        <div class="netinspect-table-wrap">
          <table class="netinspect-table">
            <thead>
              <tr>
                <th class="netinspect-col-time">Heure</th>
                <th class="netinspect-col-method">Méthode</th>
                <th>Host</th>
                <th class="netinspect-col-url">URL</th>
                <th class="netinspect-col-status">Status</th>
                <th class="netinspect-col-size">Taille</th>
                <th class="netinspect-col-dur">Durée</th>
              </tr>
            </thead>
            <tbody>
              <Show
                when={filteredTxs().length > 0}
                fallback={
                  <tr>
                    <td colspan="7" class="netinspect-empty">
                      {proxyId()
                        ? "En attente de transactions…"
                        : "Installez le certificat, configurez l'allowlist et cliquez ▶ Démarrer."}
                    </td>
                  </tr>
                }
              >
                <For each={filteredTxs()}>
                  {(tx) => (
                    <tr
                      class={`netinspect-row${tx.scheme === "tunnel" ? " netinspect-row-tunnel" : ""}${
                        tx.error ? " netinspect-row-error" : ""
                      }`}
                    >
                      <td class="netinspect-col-time">{tx.at.slice(11, 19)}</td>
                      <td class="netinspect-col-method">
                        <Show
                          when={tx.scheme !== "tunnel"}
                          fallback={<span class="netinspect-badge netinspect-method-connect">TUNNEL</span>}
                        >
                          <span
                            class={`netinspect-badge netinspect-method netinspect-method-${(
                              tx.method ?? ""
                            ).toLowerCase()}`}
                          >
                            {tx.method ?? "?"}
                          </span>
                        </Show>
                      </td>
                      <td>
                        <span class={`netinspect-scheme-${tx.scheme}`}>{tx.host}</span>
                      </td>
                      <td class="netinspect-col-url" title={tx.url ?? tx.error ?? ""}>
                        <Show when={tx.url} fallback={<span class="netinspect-muted">{tx.error ?? "—"}</span>}>
                          {tx.url}
                        </Show>
                      </td>
                      <td class={`netinspect-col-status ${statusClass(tx.status)}`}>{tx.status ? tx.status : "—"}</td>
                      <td class="netinspect-col-size">
                        {(((tx.reqBytes ?? 0) + (tx.respBytes ?? 0)) / 1024).toFixed(1)} KB
                      </td>
                      <td class="netinspect-col-dur">{tx.durationMs != null ? `${tx.durationMs} ms` : "—"}</td>
                    </tr>
                  )}
                </For>
              </Show>
            </tbody>
          </table>
        </div>
      </div>
    </PanelLayout>
  );
};

registerPanel("netinspect", NetInspectPanel);

export default NetInspectPanel;
